import React, { useState, useMemo, useCallback } from 'react';
import {
  Title2,
  Text,
  Button,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Divider,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
  DismissCircle16Filled,
  DismissRegular,
  ShieldCheckmark16Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { useMsalAuth } from '../hooks/useMsalAuth';
import { createTmdsClient } from '../services/tmds';
import { useHealthCheckData } from '../hooks/useHealthCheckData';
import { ExpandableTable } from '../components/ExpandableTable';
import type { Column } from '../components/ExpandableTable';
import IdentityManager from '../components/IdentityManager';
import { getErrorMessage } from '../utils/errors';
import DetailsFlyout from '../components/DetailsFlyout';
import ExampleViewer from '../components/ExampleViewer';
import type { DeploymentHealthEntry } from '../services/healthcheck';

// ---- Types ----
type ModelRecord = {
  id: string;
  provisioningState?: string;
  model?: Record<string, unknown>;
  capabilities?: Record<string, unknown>;
  RateLimits?: Record<string, unknown>;
  // RAI Approved Models (AB#273140): optional, read-only annotation from
  // getModelDataForApiPath.js. Present and equal to "approved" only when a
  // RUNTIMECI MODEL_APPROVAL mirror record approves this deployment for this
  // apiPath. ABSENCE is not a "rejected" signal -- it means this projection
  // could not determine an approval decision (detected/under_review/
  // rejected/not yet reviewed are indistinguishable here by design, since
  // only approved records are ever mirrored). Never render absence as a
  // negative/rejected state.
  approvalState?: string;
  approvalNotes?: string | null;
  [k: string]: unknown;
};

type ModelsPayload = Record<string, { object?: string; data?: ModelRecord[] }>;

interface ApiRow {
  apiPath: string;
  name: string;
  modelCount: number;
  healthStatus: {
    healthy: number;
    total: number;
    isActive: boolean;
    lastChecked?: string;
    failedDeployments?: Array<{ name: string; errors: string[] }>;
  } | null;
  owner?: string;
  capabilitiesSummary: string;
  deployments: ModelRecord[];
}

// ---- Helpers ----
function normalizePayload(data: unknown): ModelsPayload | null {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as ModelsPayload;
  }
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as ModelsPayload;
    } catch { /* ignore */ }
  }
  return null;
}

function extractCapabilities(deployments: ModelRecord[]): string[] {
  const caps = new Set<string>();
  for (const d of deployments) {
    if (d.capabilities && typeof d.capabilities === 'object') {
      for (const [k, v] of Object.entries(d.capabilities)) {
        if (v === true || v === 'true') caps.add(k);
      }
    }
  }
  return Array.from(caps).sort();
}

// Capabilities the apipath-testing health job actually exercised for a
// deployment (any status), trimmed and deduped case-insensitively. Used only
// to surface a resilient fallback when /tmds/models reports no capabilities.
function exercisedCapabilities(entries?: DeploymentHealthEntry[]): string[] {
  if (!entries) return [];
  const seen = new Map<string, string>();
  for (const e of entries) {
    const c = e.capability?.trim();
    if (c && !seen.has(c.toLowerCase())) seen.set(c.toLowerCase(), c);
  }
  return Array.from(seen.values()).sort();
}

// Fallback Examples capabilities: capabilities the health job exercised
// *without failing* (success/timeout — the same predicate that marks a
// deployment "Healthy"), canonicalized to the exact example-mapping keys so the
// Examples flyout opens to real examples instead of an empty list. Returns []
// when nothing maps, so the button stays hidden rather than dead.
function fallbackExampleCapabilities(
  entries: DeploymentHealthEntry[] | undefined,
  mappingKeys: string[],
): string[] {
  if (!entries || entries.length === 0 || mappingKeys.length === 0) return [];
  const byLower = new Map(mappingKeys.map((k) => [k.toLowerCase(), k]));
  const out: string[] = [];
  for (const e of entries) {
    if (e.status === 'failed') continue;
    const c = e.capability?.trim();
    if (!c) continue;
    const canonical = byLower.get(c.toLowerCase());
    if (canonical && !out.includes(canonical)) out.push(canonical);
  }
  return out;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function formatCompactRateLimits(rateLimits: unknown): string {
  if (!rateLimits || typeof rateLimits !== 'object') return '';
  const rl = rateLimits as Record<string, unknown>;
  const parts: string[] = [];

  const formatNum = (n: unknown): string => {
    if (n === 0 || n === -1 || n === null) return 'unlimited';
    if (typeof n !== 'number') return String(n);
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
    return String(n);
  };

  for (const [key, value] of Object.entries(rl)) {
    if (typeof value === 'number') {
      const label = key.toLowerCase().includes('request') ? 'req/min'
        : key.toLowerCase().includes('token') ? 'tok/min' : `${key}/min`;
      parts.push(`${formatNum(value)} ${label}`);
    } else if (typeof value === 'object' && value !== null) {
      const nested = value as Record<string, unknown>;
      if ('count' in nested && typeof nested.count === 'number') {
        const label = key.toLowerCase().includes('request') ? 'req/min'
          : key.toLowerCase().includes('token') ? 'tok/min' : `${key}/min`;
        parts.push(`${formatNum(nested.count)} ${label}`);
      }
    }
  }
  return parts.join(', ');
}

const MODELS_STORAGE_KEY = 'trapi-ui-models-cache';
const WHATS_NEW_DISMISSED_KEY = 'trapi-ui-whats-new-dismissed';

function getNewModels(currentPaths: string[]): string[] {
  const cached = localStorage.getItem(MODELS_STORAGE_KEY);
  if (!cached) {
    localStorage.setItem(MODELS_STORAGE_KEY, JSON.stringify(currentPaths));
    return [];
  }
  try {
    const prev = JSON.parse(cached) as string[];
    const prevSet = new Set(prev);
    const newOnes = currentPaths.filter((p) => !prevSet.has(p));
    localStorage.setItem(MODELS_STORAGE_KEY, JSON.stringify(currentPaths));
    return newOnes;
  } catch {
    localStorage.setItem(MODELS_STORAGE_KEY, JSON.stringify(currentPaths));
    return [];
  }
}

// ---- Styles ----
const useStyles = makeStyles({
  page: {
    padding: tokens.spacingVerticalL,
    '@media (max-width: 768px)': { paddingTop: tokens.spacingVerticalS, paddingLeft: 0, paddingRight: 0 },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
  },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, marginTop: tokens.spacingVerticalS },
  healthCell: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  statusInline: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  deploymentsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalS,
  },
  modelSubtext: { display: 'block', color: tokens.colorNeutralForeground3 },
  raiApprovedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    color: tokens.colorPaletteGreenForeground1,
    marginTop: tokens.spacingVerticalXXS,
  },
  warningText: { color: tokens.colorPaletteRedForeground1, display: 'block' },
  mobileHidden: { '@media (max-width: 768px)': { display: 'none' } },
  mutedText: { color: tokens.colorNeutralForeground3 },
  capTested: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // Subtle affordance + keyboard focusability for tooltip-only triggers.
  infoTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'help',
    textDecorationLine: 'underline',
    textDecorationStyle: 'dotted',
    textUnderlineOffset: '2px',
  },
});

// ---- Main component ----
export default function ModelsAccess() {
  const styles = useStyles();
  const { getToken, account } = useMsalAuth();
  const client = useMemo(() => createTmdsClient(getToken), [getToken]);
  const callerEmail = useMemo(() => {
    const claims = (account?.idTokenClaims as Record<string, unknown> | undefined) ?? {};
    const candidate = (
      (typeof claims.preferred_username === 'string' ? claims.preferred_username : '') ||
      (typeof claims.email === 'string' ? claims.email : '') ||
      (typeof account?.username === 'string' ? account.username : '')
    ).trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate) ? candidate : '';
  }, [account]);

  // Queries
  const myAccessQuery = useQuery({
    queryKey: ['myaccess'],
    queryFn: () => client.getMyAccess<unknown>(),
  });
  const modelsQuery = useQuery({
    queryKey: ['models'],
    queryFn: () => client.getModels<unknown>(),
  });
  const { data: healthData } = useHealthCheckData();
  const examplesMappingQuery = useQuery<{ capabilities?: Record<string, Record<string, unknown>>; substitutions?: { token: string; variable: string }[] }>({
    queryKey: ['examples-mapping'],
    queryFn: () => client.getExamplesMapping(),
    staleTime: 300_000,
  });

  // Canonical capability keys that have examples. Used to gate the resilient
  // Examples fallback so the button only appears when the health-exercised
  // capability actually maps to examples (avoids a button that opens an empty
  // flyout).
  const exampleCapabilityKeys = useMemo(
    () => Object.keys(examplesMappingQuery.data?.capabilities ?? {}),
    [examplesMappingQuery.data],
  );

  const [whatsNewDismissed, setWhatsNewDismissed] = useState(
    () => localStorage.getItem(WHATS_NEW_DISMISSED_KEY) === 'true',
  );

  // Parse access items
  const accessPaths = useMemo(() => {
    if (!myAccessQuery.data) return new Set<string>();
    const arr = Array.isArray(myAccessQuery.data) ? myAccessQuery.data : [];
    const paths = new Set<string>();
    for (const item of arr) {
      const path = (item as Record<string, unknown>).apiPath ?? (item as Record<string, unknown>).url_suffix;
      if (typeof path === 'string') paths.add(path);
    }
    return paths;
  }, [myAccessQuery.data]);

  const accessNames = useMemo(() => {
    if (!myAccessQuery.data) return new Map<string, string>();
    const arr = Array.isArray(myAccessQuery.data) ? myAccessQuery.data : [];
    const map = new Map<string, string>();
    for (const item of arr) {
      const raw = item as Record<string, unknown>;
      const path = (raw.apiPath ?? raw.url_suffix) as string | undefined;
      const name = raw.name as string | undefined;
      if (path && name) map.set(path, name);
    }
    return map;
  }, [myAccessQuery.data]);

  const accessOwners = useMemo(() => {
    if (!myAccessQuery.data) return new Map<string, string>();
    const arr = Array.isArray(myAccessQuery.data) ? myAccessQuery.data : [];
    const map = new Map<string, string>();
    for (const item of arr) {
      const raw = item as Record<string, unknown>;
      const path = (raw.apiPath ?? raw.url_suffix) as string | undefined;
      const owner = (raw.owner ?? raw.contact) as string | undefined;
      if (path && owner) map.set(path, owner);
    }
    return map;
  }, [myAccessQuery.data]);

  // Build rows
  const rows = useMemo<ApiRow[]>(() => {
    const payload = normalizePayload(modelsQuery.data);
    if (!payload) return [];

    return Object.entries(payload)
      .filter(([path]) => accessPaths.has(path) || accessPaths.has(`/${path}`) || accessPaths.size === 0)
      .map(([apiPath, group]) => {
        const deployments = group?.data ?? [];
        const normalized = apiPath.replace(/^\//, '');

        // Health from task-suite — prefer per-deployment counts (aligns with model count)
        const hd = healthData?.summary?.[normalized] ?? healthData?.summary?.[apiPath] ?? healthData?.summary?.[`/${normalized}`];
        const depHealthMap = healthData?.deploymentHealth?.[normalized]
          ?? healthData?.deploymentHealth?.[apiPath]
          ?? healthData?.deploymentHealth?.[`/${normalized}`];

        let healthStatus: ApiRow['healthStatus'] = null;

        if (depHealthMap && Object.keys(depHealthMap).length > 0) {
          // Match health data against the actual model list so counts align
          const normalizeKey = (s: string) => s.toLowerCase().replace(/[_-]/g, '');
          let healthyDeps = 0;
          const failedDeps: Array<{ name: string; errors: string[] }> = [];

          for (const dep of deployments) {
            const depEntries = depHealthMap[dep.id]
              ?? Object.entries(depHealthMap).find(
                ([key]) => normalizeKey(key) === normalizeKey(dep.id),
              )?.[1];

            if (depEntries && depEntries.length > 0) {
              const failedEntries = depEntries.filter(e => e.status === 'failed');
              if (failedEntries.length > 0) {
                failedDeps.push({
                  name: dep.id,
                  errors: failedEntries.map(e => e.error).filter((e): e is string => !!e),
                });
              } else {
                healthyDeps++;
              }
            }
          }

          healthStatus = {
            healthy: healthyDeps,
            total: deployments.length,
            isActive: hd?.isActive ?? (failedDeps.length === 0),
            lastChecked: hd?.lastChecked,
            failedDeployments: failedDeps.length > 0 ? failedDeps : undefined,
          };
        } else if (hd) {
          const msg = hd.message ?? '';
          const match = msg.match(/(\d+)\/(\d+)/);
          healthStatus = {
            healthy: match ? parseInt(match[1], 10) : 0,
            total: match ? parseInt(match[2], 10) : deployments.length,
            isActive: hd.isActive,
            lastChecked: hd.lastChecked,
          };
        }

        return {
          apiPath,
          name: accessNames.get(apiPath) || accessNames.get(`/${apiPath}`) || apiPath,
          modelCount: deployments.length,
          healthStatus,
          owner: accessOwners.get(apiPath) || accessOwners.get(`/${apiPath}`),
          capabilitiesSummary: extractCapabilities(deployments).join(', '),
          deployments,
        };
      })
      .sort((a, b) => a.apiPath.localeCompare(b.apiPath));
  }, [modelsQuery.data, accessPaths, accessNames, accessOwners, healthData]);

  // What's New
  const newModels = useMemo(() => {
    if (!rows.length) return [];
    const allDeploymentIds = rows.flatMap((r) => r.deployments.map((d) => `${r.apiPath}/${d.id}`));
    return getNewModels(allDeploymentIds);
  }, [rows]);

  const dismissWhatsNew = useCallback(() => {
    setWhatsNewDismissed(true);
    localStorage.setItem(WHATS_NEW_DISMISSED_KEY, 'true');
  }, []);

  // Stable supplemental-search callback (search-only tokens not bound to any
  // visible column - matches the placeholder's "capabilities, models..."
  // promise). MUST be useCallback'd: ExpandableTable's `processed` useMemo
  // includes getSearchableText in its deps, so an inline arrow would bust
  // the memo on every ModelsAccess re-render (flyout open/close, healthData
  // arrival, etc.) - deep-review skeptic finding on PR #94750.
  const getRowSearchableText = useCallback((r: ApiRow): string => {
    const tokens: string[] = [];
    // Uniform typeof guards: previously some fields (r.name, r.capabilitiesSummary,
    // d.id) were pushed blindly while others were guarded. If any is ever
    // undefined at runtime, String(undefined) joins as the literal "undefined"
    // which would match every search for "undefined" (false positives).
    const push = (v: unknown) => { if (typeof v === 'string' && v) tokens.push(v); };
    push(r.name);
    push(r.capabilitiesSummary);
    for (const d of r.deployments) {
      push(d.id);
      const m = d.model;
      if (m) {
        push(m.name);
        push(m.version);
        push(m.format);
      }
      push(d.provisioningState);
    }
    return tokens.join(' ');
  }, []);

  // Examples flyout
  const [examplesFlyout, setExamplesFlyout] = useState<{
    open: boolean;
    model: string;
    apiPath: string;
    capabilities: string[];
  }>({ open: false, model: '', apiPath: '', capabilities: [] });

  // Identities flyout
  const [identitiesFlyout, setIdentitiesFlyout] = useState<{ open: boolean; apiPath: string }>({ open: false, apiPath: '' });

  // Column definitions
  const columns: Column<ApiRow>[] = useMemo(
    () => [
      {
        key: 'apiPath',
        label: 'API Path',
        render: (r) => <code>{r.apiPath}</code>,
        getValue: (r) => r.apiPath,
        sortable: true,
        filterable: true,
      },
      {
        key: 'modelCount',
        label: 'Models',
        render: (r) => String(r.modelCount),
        // Include deployment IDs in searchable value so users can search by model name
        getValue: (r) => `${r.modelCount} ${r.deployments.map((d) => d.id).join(' ')}`,
        sortable: true,
        width: '80px',
        hideOnMobile: true,
      },
      {
        key: 'health',
        label: 'Health',
        render: (r) => {
          if (!r.healthStatus) {
            return (
              <Tooltip
                content="The health check job hasn't reported this API path in the latest run. This doesn't mean it's unhealthy — it just wasn't covered this cycle."
                relationship="description"
                positioning="below"
              >
                <span className={`${styles.infoTrigger} ${styles.mutedText}`} tabIndex={0}>
                  <Text size={200} className={styles.mutedText}>No test data</Text>
                </span>
              </Tooltip>
            );
          }
          const { healthy, total, isActive, lastChecked, failedDeployments } = r.healthStatus;
          const tooltipContent = (
            <div>
              <div>{healthy} of {total} deployments healthy</div>
              {failedDeployments && failedDeployments.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <strong>Failed:</strong>
                  {failedDeployments.slice(0, 5).map((f, i) => (
                    <div key={i}>• {f.name}{f.errors.length > 0 ? `: ${f.errors[0].startsWith('URL:') ? 'endpoint unreachable' : f.errors[0]}` : ''}</div>
                  ))}
                  {failedDeployments.length > 5 && <div>…and {failedDeployments.length - 5} more</div>}
                </div>
              )}
              {lastChecked && <div style={{ marginTop: 4 }}>Checked: {new Date(lastChecked).toLocaleString()}</div>}
            </div>
          );
          return (
            <Tooltip content={tooltipContent} relationship="description" positioning="below">
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, cursor: 'default' }}>
                {isActive ? (
                  <CheckmarkCircle16Filled style={{ color: tokens.colorPaletteGreenForeground1 }} />
                ) : (
                  <DismissCircle16Filled style={{ color: tokens.colorPaletteRedForeground1 }} />
                )}
                <Text size={200}>
                  {healthy}/{total}
                  {lastChecked && (
                    <span style={{ color: tokens.colorNeutralForeground3 }}> · {formatRelativeTime(lastChecked)}</span>
                  )}
                </Text>
              </div>
            </Tooltip>
          );
        },
        getValue: (r) => (!r.healthStatus ? 'No test data' : r.healthStatus.isActive ? 'Healthy' : 'Unhealthy'),
        filterable: true,
      },
    ],
    [styles],
  );

  const isLoading = myAccessQuery.isLoading || modelsQuery.isLoading;
  const error = myAccessQuery.error || modelsQuery.error;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title2>Models</Title2>
      </div>

      <Text>Models and deployments available on your TRAPI API paths.</Text>

      {/* What's New banner */}
      {newModels.length > 0 && !whatsNewDismissed && (
        <MessageBar intent="info" style={{ marginTop: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>
            <Text weight="semibold">What&apos;s New:</Text> {newModels.length} new deployment{newModels.length > 1 ? 's' : ''} since your last visit.
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="subtle" size="small" icon={<DismissRegular />} onClick={dismissWhatsNew} aria-label="Dismiss" />
          </MessageBarActions>
        </MessageBar>
      )}

      {error && (
        <Text style={{ color: tokens.colorPaletteRedForeground1, display: 'block', marginTop: tokens.spacingVerticalS }}>{getErrorMessage(error)}</Text>
      )}

      <div style={{ marginTop: tokens.spacingVerticalM }}>
        <ExpandableTable<ApiRow>
          columns={columns}
          items={rows}
          getRowKey={(r) => r.apiPath}
          loading={isLoading}
          emptyMessage="No API paths found. Check your access."
          searchPlaceholder="Search API paths, capabilities, models…"
          getSearchableText={getRowSearchableText}
          renderExpanded={(row, searchText) => {
            const normalized = row.apiPath.replace(/^\//, '');
            const depHealthMap = healthData?.deploymentHealth?.[normalized]
              ?? healthData?.deploymentHealth?.[row.apiPath]
              ?? healthData?.deploymentHealth?.[`/${normalized}`];
            return (
              <DeploymentsPanel
                row={row}
                onManageIdentities={(apiPath) => setIdentitiesFlyout({ open: true, apiPath })}
                onViewExample={(caps, model) =>
                  setExamplesFlyout({ open: true, model, apiPath: row.apiPath, capabilities: caps })
                }
                deploymentHealth={depHealthMap}
                exampleCapabilityKeys={exampleCapabilityKeys}
                searchText={searchText}
              />
            );
          }}
        />
      </div>

      {/* Examples Flyout */}
      <DetailsFlyout
        open={examplesFlyout.open}
        onClose={() => setExamplesFlyout((p) => ({ ...p, open: false }))}
        title="Examples"
        size="large"
      >
        {examplesFlyout.open && (
          <ExampleViewer
            capabilities={examplesFlyout.capabilities}
            capabilityMapping={examplesMappingQuery.data?.capabilities}
            model={examplesFlyout.model}
            apiPath={examplesFlyout.apiPath}
            getToken={getToken}
          />
        )}
      </DetailsFlyout>

      {/* Identities Flyout */}
      <DetailsFlyout
        open={identitiesFlyout.open}
        onClose={() => setIdentitiesFlyout({ open: false, apiPath: '' })}
        title={`Identities — ${identitiesFlyout.apiPath}`}
        size="medium"
      >
        {identitiesFlyout.open && (
          <IdentityManager apiPath={identitiesFlyout.apiPath} client={client} callerEmail={callerEmail} />
        )}
      </DetailsFlyout>
    </div>
  );
}

// ---- Deployments expanded panel ----
function DeploymentsPanel({
  row,
  onManageIdentities,
  onViewExample,
  deploymentHealth,
  exampleCapabilityKeys,
  searchText,
}: {
  row: ApiRow;
  onManageIdentities: (apiPath: string) => void;
  onViewExample: (capabilities: string[], model: string) => void;
  deploymentHealth?: Record<string, DeploymentHealthEntry[]>;
  exampleCapabilityKeys: string[];
  searchText?: string;
}) {
  const styles = useStyles();
  // Filter deployments by the top-level search box so when a user searches
  // "5.4" they see only deployments whose id/model/capabilities match, not
  // all 79 deployments under a matching API path.
  // Empty/whitespace search shows everything (unchanged behavior).
  const q = (searchText ?? '').trim().toLowerCase();
  const visibleDeployments = q.length === 0
    ? row.deployments
    : row.deployments.filter((d) => {
        if (d.id?.toLowerCase().includes(q)) return true;
        const model = d.model as Record<string, unknown> | undefined;
        if (model && typeof model.name === 'string' && model.name.toLowerCase().includes(q)) return true;
        if (d.capabilities && typeof d.capabilities === 'object') {
          const capStr = Object.entries(d.capabilities)
            .filter(([, v]) => v === true || v === 'true')
            .map(([k]) => k)
            .join(' ')
            .toLowerCase();
          if (capStr.includes(q)) return true;
        }
        return false;
      });
  return (
    <div>
      {/* API path details */}
      {row.owner && (
        <Text size={200} style={{ display: 'block', marginBottom: tokens.spacingVerticalXS }}>
          <strong>Owner:</strong> {row.owner}
        </Text>
      )}
      {row.healthStatus?.lastChecked && (
        <Text size={200} style={{ display: 'block', marginBottom: tokens.spacingVerticalS }}>
          <strong>Last Checked:</strong>{formatRelativeTime(row.healthStatus.lastChecked)}{' '}
          ({new Date(row.healthStatus.lastChecked).toLocaleString()})
        </Text>
      )}
      <Divider style={{ marginBottom: tokens.spacingVerticalM }} />

      <div className={styles.deploymentsHeader}>
        <Text weight="semibold">
          {visibleDeployments.length} of {row.deployments.length} Deployment{row.deployments.length === 1 ? '' : 's'}
          {q.length > 0 && visibleDeployments.length !== row.deployments.length ? ` matching "${searchText}"` : ''}
        </Text>
        <Button
          size="small"
          onClick={() => onManageIdentities(row.apiPath)}
        >
          Manage Identities
        </Button>
      </div>

      <div style={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Deployments" style={{ tableLayout: 'auto' }}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: '140px' }}>Status</TableHeaderCell>
            <TableHeaderCell>Deployment</TableHeaderCell>
            <TableHeaderCell className={styles.mobileHidden}>Capabilities</TableHeaderCell>
            <TableHeaderCell className={styles.mobileHidden}>Rate Limits</TableHeaderCell>
            <TableHeaderCell style={{ width: '80px' }}>Actions</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleDeployments.map((d, idx) => {
            const caps = d.capabilities && typeof d.capabilities === 'object'
              ? Object.entries(d.capabilities).filter(([, v]) => v === true || v === 'true').map(([k]) => k)
              : [];
            const model = d.model as Record<string, unknown> | undefined;

            // Deployment health from task-suite results
            // The health data keys use deploymentName which may differ from d.id
            // in casing or dash/underscore. Try exact match first, then normalized.
            const normalize = (s: string) => s.toLowerCase().replace(/[_-]/g, '');
            const depHealth = deploymentHealth?.[d.id]
              ?? (deploymentHealth
                ? Object.entries(deploymentHealth).find(
                    ([key]) => normalize(key) === normalize(d.id),
                  )?.[1]
                : undefined);
            let healthLabel: 'healthy' | 'unhealthy' | 'unknown' = 'unknown';
            if (depHealth && depHealth.length > 0) {
              const anyFailed = depHealth.some((e) => e.status === 'failed');
              healthLabel = anyFailed ? 'unhealthy' : 'healthy';
            }

            // Resilience: /tmds/models sometimes reports no capabilities for a
            // deployment the health job tested fine (catalog config drift). Show
            // what the job exercised and let Examples fall back to it.
            const exercised = caps.length === 0 ? exercisedCapabilities(depHealth) : [];
            const fallbackCaps = caps.length === 0
              ? fallbackExampleCapabilities(depHealth, exampleCapabilityKeys)
              : [];
            const exampleCaps = caps.length > 0 ? caps : fallbackCaps;

            const provState = d.provisioningState ?? 'Unknown';

            return (
              <TableRow key={`${d.id}-${idx}`}>
                <TableCell>
                  <div>
                    <span className={styles.statusInline}>
                      {healthLabel === 'healthy' ? (
                        <>
                          <CheckmarkCircle16Filled style={{ color: tokens.colorPaletteGreenForeground1 }} />
                          <Text size={200} style={{ color: tokens.colorPaletteGreenForeground1 }}>Healthy</Text>
                        </>
                      ) : healthLabel === 'unhealthy' ? (
                        <Tooltip
                          content={
                            <div>
                              {depHealth?.filter(e => e.status === 'failed').map((e, i) => (
                                <div key={i}>
                                  {e.error
                                    ? e.error.startsWith('URL:')
                                      ? 'Endpoint unreachable'
                                      : e.error
                                    : 'Health check failed'}
                                </div>
                              ))}
                            </div>
                          }
                          relationship="description"
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, cursor: 'default' }}>
                            <DismissCircle16Filled style={{ color: tokens.colorPaletteRedForeground1 }} />
                            <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>Unhealthy</Text>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          content="This deployment wasn't covered in the latest health check run. It isn't necessarily unhealthy — the daily job didn't test it this cycle."
                          relationship="description"
                        >
                          <span className={`${styles.infoTrigger} ${styles.mutedText}`} tabIndex={0}>
                            <Text size={200} className={styles.mutedText}>— Not tested</Text>
                          </span>
                        </Tooltip>
                      )}
                    </span>
                    {provState !== 'Succeeded' && (
                      <Text size={100} className={styles.warningText}>⚠️ {provState}</Text>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Text weight="semibold"><code>{d.id}</code></Text>
                  {model && (
                    <Text size={200} className={styles.modelSubtext}>
                      {(model.name as string) || ''} {model.version ? `v${model.version}` : ''}
                    </Text>
                  )}
                  {d.approvalState === 'approved' && (
                    <Tooltip
                      content={
                        d.approvalNotes
                          ? `RAI Approved Models: ${d.approvalNotes}`
                          : 'RAI Approved Models: approved for use on this API path.'
                      }
                      relationship="description"
                    >
                      <span className={`${styles.raiApprovedBadge} ${styles.infoTrigger}`} tabIndex={0}>
                        <ShieldCheckmark16Regular />
                        <Text size={200}>RAI Approved</Text>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell className={styles.mobileHidden}>
                  {caps.length > 0 ? (
                    <Text size={200}>{caps.join(', ')}</Text>
                  ) : exercised.length > 0 ? (
                    <Tooltip
                      content={`The model catalog doesn't list capabilities for this deployment. The latest health check exercised: ${exercised.join(', ')}. The model may support additional capabilities.`}
                      relationship="description"
                    >
                      <span className={`${styles.infoTrigger} ${styles.capTested}`} tabIndex={0}>
                        <Text size={200} className={styles.capTested}>Tested: {exercised.join(', ')}</Text>
                      </span>
                    </Tooltip>
                  ) : (
                    <Text size={200}>—</Text>
                  )}
                </TableCell>
                <TableCell className={styles.mobileHidden}>
                  <Text size={200}>{formatCompactRateLimits(d.RateLimits) || '—'}</Text>
                </TableCell>
                <TableCell>
                  {exampleCaps.length > 0 && (
                    <Button size="small" appearance="outline" onClick={() => onViewExample(exampleCaps, d.id)}>
                      Examples
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}

 